import React, { useState, useCallback } from 'react';
import { AutoComplete, Input, Spin, Tag, Space } from 'antd';
import { BankOutlined, SearchOutlined } from '@ant-design/icons';
import { AXIOS_INSTANCE } from '@/shared/api/mutator/custom-instance';
import { debounce } from 'lodash';

export interface GlAccountOption {
  glNo: string;
  acNo?: string;
  accountId: string;
  glName: string;
  acName: string;
  acNature: string;
}

interface GlAccountPickerProps {
  /** 'GL' = search GL headers, 'AC' = search member/customer accounts within a glNo */
  mapType?: 'GL' | 'AC';
  /** When mapType is AC, restrict search within this GL */
  glNo?: string;
  value?: string;
  onChange?: (value: string, option?: GlAccountOption) => void;
  onAccountSelect?: (option: GlAccountOption) => void;
  disabled?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
}

const GlAccountPicker: React.FC<GlAccountPickerProps> = ({
  mapType = 'GL',
  glNo,
  value,
  onChange,
  onAccountSelect,
  disabled = false,
  placeholder,
  style,
}) => {
  const [options, setOptions] = useState<{ value: string; label: React.ReactNode; raw: GlAccountOption }[]>([]);
  const [loading, setLoading] = useState(false);

  const doSearch = useCallback(
    debounce(async (q: string) => {
      if (!q || q.length < 1) {
        setOptions([]);
        return;
      }
      setLoading(true);
      try {
        const params: Record<string, string> = { q };
        if (glNo && mapType === 'AC') params.glNo = glNo;
        const resp = await AXIOS_INSTANCE.get<GlAccountOption[]>(
          `/api/coas/accounts/${mapType}`,
          { params }
        );
        const accounts: GlAccountOption[] = (resp.data || []).map((a: any) => ({
          glNo: a.accountId?.split('.')?.[0] || glNo || '',
          acNo: a.accountId?.split('.')?.[1],
          accountId: a.accountId,
          glName: a.glName || '',
          acName: a.acName || '',
          acNature: a.acNature || '',
        }));
        setOptions(
          accounts.map((acc) => ({
            value: acc.accountId,
            raw: acc,
            label: (
              <div className="flex items-center justify-between gap-2 py-0.5">
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-slate-800 truncate block">
                    {acc.acName || acc.glName}
                  </span>
                  <span className="text-xs text-slate-400">{acc.accountId}</span>
                </div>
                <Tag color="blue" className="text-xs flex-shrink-0">{acc.acNature}</Tag>
              </div>
            ),
          }))
        );
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [glNo, mapType]
  );

  return (
    <AutoComplete
      value={value}
      options={options}
      onSearch={doSearch}
      onSelect={(val, opt) => {
        onChange?.(val);
        onAccountSelect?.((opt as any).raw);
      }}
      onChange={(val) => {
        onChange?.(val);
        if (!val) {
          onAccountSelect?.({ glNo: '', acNo: '', accountId: '', glName: '', acName: '', acNature: '' });
        }
      }}
      disabled={disabled}
      style={{ width: '100%', ...style }}
      dropdownStyle={{ minWidth: 300 }}
    >
      <Input
        prefix={loading ? <Spin size="small" /> : <BankOutlined className="text-slate-400" />}
        suffix={<SearchOutlined className="text-slate-400" />}
        placeholder={placeholder || (mapType === 'AC' && glNo ? `Search accounts in GL ${glNo}` : 'Search GL / account...')}
        allowClear
      />
    </AutoComplete>
  );
};

export default GlAccountPicker;
